import React, { useState, useEffect } from 'react';
import {
  Select,
  MenuItem,
  TextField,
  Button,
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Grid,
  IconButton,
  Snackbar,
  Chip,
  Box,
  Divider,
  Fade,
  InputLabel,
  FormControl,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CalculateIcon from '@mui/icons-material/Calculate';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { group_and_criminal_score_to_months, groupToHistoryScore, offenseToSeverityGroup } from './constants';

function App() {
  const [currentOffense, setCurrentOffense] = useState('');
  const [offenses, setOffenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sentenceRange, setSentenceRange] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  useEffect(() => {
    document.title = "DC Sentencing";
  }, []); // The empty array causes this effect to only run on mount

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const addOffense = (offense) => {
    if (!offenses.includes(offense)) {
      setOffenses([...offenses, offense]);
      setSnackbarOpen(true);
    }
  };

  const handleDeleteOffense = (offense) => {
    setOffenses(offenses.filter((o) => o !== offense));
  };

  const calculateSentence = () => {
    setIsCalculating(true);

    // Simulate calculation time for better UX (Material Design: Motion provides meaning)
    setTimeout(() => {
      let criminalHistoryScore = offenses.reduce((score, offense) => {
        const group = offenseToSeverityGroup[offense];
        return score + groupToHistoryScore[`Group ${group}`];
      }, 0);

      const currentOffenseGroup = offenseToSeverityGroup[currentOffense];
      const ranges = group_and_criminal_score_to_months[`Group ${currentOffenseGroup}`];

      // Find the correct range
      let matchedRange = null;
      for (const [range, sentence] of Object.entries(ranges)) {
        const [min, max] = range.split(' to ').map(parseFloat);
        if ((criminalHistoryScore >= min && criminalHistoryScore <= max) ||
            (range.endsWith('+') && criminalHistoryScore >= min)) {
          matchedRange = sentence;
          break;
        }
      }

      // matchedRange will be a string like "90 - 180" or "138+"
      // You might want to further process it to fit your needs.
      setSentenceRange(matchedRange ? `${matchedRange} months` : 'Score range not found');
      setIsCalculating(false);
    }, 400);
  };

  const filteredOffenses = Object.keys(offenseToSeverityGroup)
    .filter((offense) => offense.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        {/* Header Section - Material Design: Clear Hierarchy */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            align="center"
            sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}
          >
            DC Sentencing Guidelines Calculator
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            align="center"
            sx={{ mb: 3 }}
          >
            Designed to assist defense lawyers and legal professionals in understanding sentencing ranges based on the District of Columbia Sentencing and Criminal Code. Guidelines reflect the{' '}
            <a
              href="https://scdc.dc.gov/page/sentencing-guidelines-manual-sccrc"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}
            >
              Sentencing Commission for the District of Columbia
            </a>{' '}
            for 2023.
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Instructions Section - Material Design: Informative, Scannable */}
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon />}
          sx={{ mb: 3 }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            How to Use:
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Search and add all convicted offenses using the left panel
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
              Select the current offense from the dropdown on the right
            </Typography>
            <Typography component="li" variant="body2">
              Click Calculate to view the recommended sentencing range
            </Typography>
          </Box>
        </Alert>
        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Left Column: Offense Search & Selection */}
          <Grid item xs={12} md={6}>
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRight: { md: '1px solid rgba(0, 0, 0, 0.12)' },
              pr: { md: 2 }
            }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Past offenses committed
              </Typography>

              <TextField
                label="Search Offenses"
                variant="outlined"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                placeholder="Type to filter..."
                InputProps={{
                  endAdornment: <SearchIcon color="action" />
                }}
                sx={{ mb: 2 }}
              />

              <List
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  maxHeight: 400,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  p: 1
                }}
              >
                {filteredOffenses.length > 0 ? (
                  filteredOffenses.map((offense) => (
                    <Fade in key={offense} timeout={300}>
                      <ListItem
                        onClick={() => addOffense(offense)}
                        disabled={offenses.includes(offense)}
                        sx={{
                          cursor: offenses.includes(offense) ? 'default' : 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': offenses.includes(offense) ? {} : {
                            bgcolor: 'primary.light',
                            color: 'primary.contrastText',
                            '& .MuiSvgIcon-root': {
                              color: 'primary.contrastText'
                            }
                          }
                        }}
                      >
                        <ListItemText
                          primary={offense}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: { fontWeight: offenses.includes(offense) ? 400 : 500 }
                          }}
                        />
                        <AddCircleOutlineIcon
                          color={offenses.includes(offense) ? "disabled" : "primary"}
                        />
                      </ListItem>
                    </Fade>
                  ))
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No offenses found
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
          </Grid>
          {/* Right Column: Selected Offenses & Calculation */}
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                Selected Offenses
              </Typography>

              {/* Material Design: Chips for selected items */}
              <Box
                sx={{
                  minHeight: 120,
                  bgcolor: 'background.default',
                  borderRadius: 2,
                  p: 2,
                  mb: 3,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  alignContent: 'flex-start'
                }}
              >
                {offenses.length > 0 ? (
                  offenses.map((offense, index) => (
                    <Fade in key={offense + index} timeout={300}>
                      <Chip
                        label={offense}
                        onDelete={() => handleDeleteOffense(offense)}
                        color="primary"
                        variant="filled"
                        deleteIcon={<CloseIcon />}
                        sx={{
                          maxWidth: '100%',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }
                        }}
                      />
                    </Fade>
                  ))
                ) : (
                  <Box sx={{ width: '100%', textAlign: 'center', py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No offenses selected yet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Click offenses from the left to add them
                    </Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 3 }} />

              {/* Current Offense Selection - Material Design: Proper label */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="current-offense-label">Current charge</InputLabel>
                <Select
                  labelId="current-offense-label"
                  id="current-offense-select"
                  label="Current charge"
                  value={currentOffense}
                  onChange={(e) => setCurrentOffense(e.target.value)}
                  variant="outlined"
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {Object.keys(offenseToSeverityGroup).map((offense) => (
                    <MenuItem key={offense} value={offense}>
                      {offense}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Calculate Button with Loading State */}
              <Button
                startIcon={isCalculating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
                variant="contained"
                color="primary"
                onClick={calculateSentence}
                disabled={!currentOffense || isCalculating}
                fullWidth
                size="large"
                sx={{ py: 1.5 }}
              >
                {isCalculating ? 'Calculating...' : 'Calculate Sentencing Range'}
              </Button>

              {/* Results Section with Fade Animation */}
              {sentenceRange && (
                <Fade in timeout={500}>
                  <Paper
                    elevation={0}
                    sx={{
                      mt: 3,
                      p: 3,
                      textAlign: 'center',
                      bgcolor: 'success.light',
                      color: 'success.contrastText',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="overline" display="block" sx={{ mb: 1, opacity: 0.8 }}>
                      Recommended Sentencing Range
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {sentenceRange}
                    </Typography>
                  </Paper>
                </Fade>
              )}
            </Box>
          </Grid>
        </Grid>
        {/* Snackbar Feedback - Material Design: Use CloseIcon */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="success"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Offense added successfully
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}

export default App;
